import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { UserAccountTicketsConfigurationRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

const usePortfolioTicketsMarkAsRead = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (query: ApiQueryParams<"postAdminPortfolioTicketsReadV1"> | undefined = undefined) =>
      api.postAdminPortfolioTicketsReadV1({ ...query }),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.tickets.mark-all-as-read.success") });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.mark-all-as-read.error") });
    },
  });
};

const usePortfolioTicketUpdateSettings = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (req: UserAccountTicketsConfigurationRequest) => api.putUserProfileTicketsConfigurationV1(req),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKET_SETTINGS,
      });
      showFlashToast({
        variant: "success",
        message: t("page.ticket-settings.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.ticket-settings.error"),
      });
    },
  });
};

const useMarkPortfolioTicketDetailsAsRead = (ticketId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: () => api.postTicketsReadV1(ticketId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
    },
  });
};

export const portfolioTicketsMutations = {
  usePortfolioTicketsMarkAsRead,
  usePortfolioTicketUpdateSettings,
  useMarkPortfolioTicketDetailsAsRead,
};
